import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import {
  Loader2, ArrowRight, CheckCircle2, Clock, Award, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  TYPE_META,
  SpeakingPlayer,
  ReadCompletePlayer,
  WritingPlayer,
  MCQPlayer,
  ListenTypePlayer,
} from "./ExamPlayer";

const EXAM_TIME_LIMITS = {
  pte: 1800,      // 30 min for the abbreviated mock
  duolingo: 1500, // 25 min
  oxford: 1800,   // 30 min
};

const EXAM_META = {
  pte:      { name: "PTE Academic",        color: "#0C4B66" },
  duolingo: { name: "Duolingo English",    color: "#16A34A" },
  oxford:   { name: "Oxford ELLT",         color: "#7C3AED" },
};

export default function MockRunner({ examIdOverride }) {
  const params = useParams();
  const examId = examIdOverride || params.examId;
  const nav = useNavigate();
  const [stage, setStage] = useState("brief"); // brief | running | finished
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [final, setFinal] = useState(null);
  const [timeLeft, setTimeLeft] = useState(EXAM_TIME_LIMITS[examId] || 1800);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);
  const exam = EXAM_META[examId];

  const begin = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/exam/${examId}/mock/start`);
      setSession(r.data);
      setIdx(0);
      setStage("running");
      setTimeLeft(EXAM_TIME_LIMITS[examId] || 1800);
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { finalize(); return 0; }
          return t - 1;
        });
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start mock test");
    } finally {
      setLoading(false);
    }
  };

  const onItemComplete = async (result) => {
    if (!session) return;
    const cur = session.items[idx];
    try {
      await api.post(`/exam/mock/${session.session_id}/record-item`, {
        type_id: cur.type_id, item_id: cur.item.id, result,
      });
    } catch (e) {
      console.warn("Mock record failed", e);
    }
    if (idx + 1 >= session.items.length) {
      await finalize();
    } else {
      setIdx((i) => i + 1);
    }
  };

  const finalize = async () => {
    if (!session) return;
    clearInterval(timerRef.current);
    setLoading(true);
    try {
      const r = await api.post(`/exam/mock/${session.session_id}/finalize`);
      setFinal(r.data);
      setStage("finished");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Final scoring failed");
    } finally {
      setLoading(false);
    }
  };

  if (!exam) return <DashboardLayout><div className="text-slate-500">Unknown exam.</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid={`mock-runner-${examId}`}>
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl p-8 text-white"
          style={{ background: `linear-gradient(135deg, ${exam.color} 0%, #003049 100%)` }}>
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-1">Mock Test</div>
              <h1 className="font-display text-4xl">{exam.name} — Full Practice Mock</h1>
              <div className="text-sm text-white/80 mt-2">Timed · scored end-to-end · AI evaluation</div>
            </div>
            {stage === "running" && (
              <div className="px-5 py-3 rounded-2xl bg-white/15 backdrop-blur" data-testid="mock-timer">
                <div className="text-[10px] uppercase tracking-wider text-white/70">Time remaining</div>
                <div className="font-mono text-3xl tabular-nums">
                  {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:{String(timeLeft % 60).padStart(2, "0")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Brief */}
        {stage === "brief" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 space-y-5">
            <div className="flex items-center gap-3 text-[#003049]">
              <Clock className="w-6 h-6 text-[#F77F00]" />
              <h2 className="font-display text-2xl">Before you begin</h2>
            </div>
            <ul className="space-y-2 text-slate-700 text-sm">
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#F77F00] shrink-0 mt-0.5" /> The mock contains 4–5 timed tasks across all skills.</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#F77F00] shrink-0 mt-0.5" /> You'll be graded with the same AI rubric used in real practice.</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#F77F00] shrink-0 mt-0.5" /> Have your microphone enabled — speaking tasks are included.</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#F77F00] shrink-0 mt-0.5" /> Take the test in a quiet room without distractions.</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#F77F00] shrink-0 mt-0.5" /> You cannot pause once started.</li>
            </ul>
            <button onClick={begin} disabled={loading} data-testid="begin-mock-btn"
              className="mt-4 px-7 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
              Begin Mock Test
            </button>
          </div>
        )}

        {/* Running */}
        {stage === "running" && session && (
          <>
            {/* Progress */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mock progress</div>
                <div className="text-sm text-slate-700">{idx + 1} / {session.items.length}</div>
              </div>
              <div className="flex gap-1.5">
                {session.items.map((it, i) => (
                  <div key={i} className={`flex-1 h-2 rounded-full ${
                    i < idx ? "bg-[#16A34A]" : i === idx ? "bg-[#F77F00]" : "bg-slate-200"
                  }`} />
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] uppercase tracking-wider text-slate-500">
                {session.items.map((it, i) => (
                  <div key={i} className={i === idx ? "font-bold text-[#F77F00]" : ""}>{it.type_id.replace("-", " ")}</div>
                ))}
              </div>
            </div>

            {/* Active item — delegate to existing player */}
            <MockItem
              key={`${session.items[idx].type_id}-${session.items[idx].item.id}`}
              examId={examId}
              entry={session.items[idx]}
              onComplete={onItemComplete}
            />
          </>
        )}

        {/* Finished */}
        {stage === "finished" && final && (
          <div className="bg-gradient-to-br from-[#003049] to-[#0C4B66] text-white rounded-3xl p-8" data-testid="mock-final">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">Final Mock Score</div>
                <div className="font-display text-7xl">{final.overall}</div>
                <div className="text-sm text-white/70 mt-1">{exam.name} · {final.scale}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setStage("brief"); setFinal(null); setSession(null); }}
                  className="px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-sm">
                  Take again
                </button>
                <button onClick={() => nav("/app")} className="px-4 py-2 rounded-full bg-white text-[#003049] text-sm font-semibold inline-flex items-center gap-2">
                  Dashboard <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(final.by_skill || {}).map(([skill, score]) => (
                <div key={skill} className="rounded-xl bg-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/70">{skill}</div>
                  <div className="font-display text-3xl mt-1">{score !== null ? Math.round(score) : "—"}</div>
                  <div className="text-xs text-white/60 mt-1">{score !== null ? "/100" : "not attempted"}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-sm text-white/80">
              Completed {final.completed_items}/{final.total_items} items · +80 XP
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

/**
 * MockItem — renders one item using the same primitive players as ExamPlayer,
 * but listens for results and auto-advances to the next item in the mock.
 */
function MockItem({ examId, entry, onComplete }) {
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Auto-advance after a brief confirmation
  useEffect(() => {
    if (result) {
      const t = setTimeout(() => onComplete(result), 1500);
      return () => clearTimeout(t);
    }
  }, [result]); // eslint-disable-line

  const typeId = entry.type_id;
  const item = entry.item;
  const meta = TYPE_META[typeId] || {};
  const common = { examId, typeId, item, meta, onResult: setResult, submitting, setSubmitting };

  return (
    <div>
      {meta.skill === "speaking" && <SpeakingPlayer {...common} />}
      {typeId === "read-complete" && <ReadCompletePlayer {...common} />}
      {(typeId === "write-photo" || typeId === "writing-task" || typeId === "summarise-text") && <WritingPlayer {...common} />}
      {(typeId === "reading-mcq" || typeId === "listening-mcq") && <MCQPlayer {...common} />}
      {typeId === "listen-type" && <ListenTypePlayer {...common} />}
      {result && (
        <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Recorded. Moving to next task…
        </div>
      )}
    </div>
  );
}
